import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  IconButton,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import EventsApiService from '../../services/events/EventsApiService';
import DashboardApiService from '../../services/dashboard/DashboardApiService';

const Events = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [eventsData, setEventsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchingAgents, setFetchingAgents] = useState(true);
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('24h');

  // Fetch agents on component mount
  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setFetchingAgents(true);
      const agentsData = await DashboardApiService.getAllAgents();
      setAgents(agentsData || []);
      
      // Set default agent selection to first agent
      if (agentsData && agentsData.length > 0 && !selectedAgent) {
        setSelectedAgent(agentsData[0].id);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setError('Failed to fetch agents');
    } finally {
      setFetchingAgents(false);
    }
  };

  const handleFetchEvents = async () => {
    if (!selectedAgent) {
      setError('Please select an agent');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await EventsApiService.getEventsEvolution(selectedAgent);
      setEventsData(response);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to fetch events data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = (events) => {
    if (!events || !Array.isArray(events)) return [];
    
    return events.map(event => ({
      timestamp: new Date(event.timestamp).toLocaleString(),
      count: event.count,
      shortTime: new Date(event.timestamp).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        month: 'short',
        day: 'numeric'
      }),
      date: new Date(event.timestamp).toLocaleDateString(),
      hour: new Date(event.timestamp).getHours()
    }));
  };

  const getEventStatistics = (events) => {
    if (!events || events.length === 0) return null;
    
    const totalEvents = events.reduce((sum, event) => sum + event.count, 0);
    const avgEventsPerHour = Math.round(totalEvents / events.length);
    const maxEvents = Math.max(...events.map(e => e.count));
    const minEvents = Math.min(...events.map(e => e.count));
    const peakHour = events.reduce((max, event) => 
      event.count > max.count ? event : max, events[0]);
    
    return {
      totalEvents,
      avgEventsPerHour,
      maxEvents,
      minEvents,
      peakHour: new Date(peakHour.timestamp).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      peakCount: peakHour.count
    };
  };

  const getHourlyDistribution = (events) => {
    if (!events || events.length === 0) return [];
    
    const hourlyData = {};
    events.forEach(event => {
      const hour = new Date(event.timestamp).getHours();
      hourlyData[hour] = (hourlyData[hour] || 0) + event.count;
    });
    
    return Object.entries(hourlyData).map(([hour, count]) => ({
      hour: `${hour}:00`,
      count,
      name: `${hour}:00`
    })).sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  };

  const chartData = eventsData ? formatChartData(eventsData.events) : [];
  const statistics = eventsData ? getEventStatistics(eventsData.events) : null;
  const hourlyData = eventsData ? getHourlyDistribution(eventsData.events) : [];

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00'];

  const renderChart = () => {
    if (!chartData.length) return null;

    const commonProps = {
      data: chartType === 'hourly' ? hourlyData : chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 60 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="shortTime"
              angle={-45}
              textAnchor="end"
              height={80}
              fontSize={12}
            />
            <YAxis fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value) => [value.toLocaleString(), 'Events']}
            />
            <Area 
              type="monotone" 
              dataKey="count" 
              stroke="#8884d8" 
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorEvents)"
            />
          </AreaChart>
        );
      
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="shortTime"
              angle={-45}
              textAnchor="end"
              height={80}
              fontSize={12}
            />
            <YAxis fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value) => [value.toLocaleString(), 'Events']}
            />
            <Bar dataKey="count" fill="#8884d8" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      
      case 'hourly':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="hour" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value) => [value.toLocaleString(), 'Events']}
            />
            <Bar dataKey="count" fill="#82ca9d" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      
      default: // line
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="shortTime"
              angle={-45}
              textAnchor="end"
              height={80}
              fontSize={12}
            />
            <YAxis fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value) => [value.toLocaleString(), 'Events']}
            />
            <Line 
              type="monotone" 
              dataKey="count" 
              stroke="#8884d8" 
              strokeWidth={3}
              dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#8884d8', strokeWidth: 2 }}
            />
          </LineChart>
        );
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          <EventIcon sx={{ mr: 2, verticalAlign: 'middle', color: '#6366f1' }} />
          Events Evolution Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Monitor and analyze security events across your infrastructure
        </Typography>
      </Box>

      {/* Control Panel */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={9}>
              <FormControl fullWidth disabled={fetchingAgents}>
                <Select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Select an Agent to Analyze Events
                        </Typography>
                      );
                    }
                    const agent = agents.find(a => a.id === selected);
                    return agent ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip 
                          icon={<EventIcon />} 
                          label={agent.name} 
                          size="small" 
                          sx={{ mr: 2, fontWeight: 'bold' }} 
                        />
                        <Typography variant="body1" color="text.secondary">
                          {agent.ip}
                        </Typography>
                      </Box>
                    ) : selected;
                  }}
                  sx={{ 
                    backgroundColor: 'white',
                    height: '56px',
                    fontSize: '1rem',
                    '& .MuiSelect-select': {
                      padding: '16px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '56px',
                      boxSizing: 'border-box'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '2px',
                      borderColor: '#d1d5db'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left'
                    }
                  }}
                >
                  {agents.map((agent) => (
                    <MenuItem 
                      key={agent.id} 
                      value={agent.id}
                      sx={{ 
                        minHeight: '56px',
                        padding: '12px 16px',
                        '&:hover': {
                          backgroundColor: '#f3f4f6'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Chip 
                          icon={<EventIcon />} 
                          label={agent.name} 
                          size="small" 
                          sx={{ mr: 2, fontWeight: 'bold' }} 
                        />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {agent.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            IP: {agent.ip} • Status: {agent.status || 'Active'}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={handleFetchEvents}
                disabled={!selectedAgent || loading || fetchingAgents}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <BarChartIcon />}
                sx={{ 
                  height: '56px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4f46e5, #7c3aed)',
                  }
                }}
              >
                {loading ? 'Loading...' : 'ANALYZE EVENTS'}
              </Button>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <MuiTooltip title="Refresh Data">
                  <IconButton 
                    onClick={() => selectedAgent && handleFetchEvents()}
                    disabled={!selectedAgent || loading}
                    sx={{ backgroundColor: 'white' }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </MuiTooltip>
                
                <ToggleButtonGroup
                  value={chartType}
                  exclusive
                  onChange={(e, newType) => newType && setChartType(newType)}
                  size="small"
                  sx={{ backgroundColor: 'white', borderRadius: 2 }}
                >
                  <ToggleButton value="line">
                    <MuiTooltip title="Line Chart">
                      <ShowChartIcon />
                    </MuiTooltip>
                  </ToggleButton>
                  <ToggleButton value="area">
                    <MuiTooltip title="Area Chart">
                      <TimelineIcon />
                    </MuiTooltip>
                  </ToggleButton>
                  <ToggleButton value="bar">
                    <MuiTooltip title="Bar Chart">
                      <BarChartIcon />
                    </MuiTooltip>
                  </ToggleButton>
                  <ToggleButton value="hourly">
                    <MuiTooltip title="Hourly Distribution">
                      <ScheduleIcon />
                    </MuiTooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
          </Grid>

          {fetchingAgents && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, p: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Loading agents...</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.totalEvents.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Events
                    </Typography>
                  </Box>
                  <EventIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.avgEventsPerHour.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Avg/Hour
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.peakCount.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Peak at {statistics.peakHour}
                    </Typography>
                  </Box>
                  <ScheduleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {statistics.maxEvents.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Max Events
                    </Typography>
                  </Box>
                  <AnalyticsIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Chart Section */}
      {eventsData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Events Analysis for Agent {eventsData.agent_id}
              </Typography>
              <Chip 
                label={`${chartType.charAt(0).toUpperCase() + chartType.slice(1)} View`}
                color="primary"
                variant="outlined"
              />
            </Box>
            
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ width: '100%', height: 450 }}>
              <ResponsiveContainer>
                {renderChart()}
              </ResponsiveContainer>
            </Box>

            {chartData.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Events Data Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No events data found for the selected agent in the specified time range.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions Card */}
      {!eventsData && !loading && (
        <Card sx={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <EventIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Welcome to Events Evolution Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Select an agent from the dropdown above and click "Analyze Events" to view detailed event analytics
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip icon={<ShowChartIcon />} label="Line Charts" variant="outlined" />
              <Chip icon={<TimelineIcon />} label="Area Charts" variant="outlined" />
              <Chip icon={<BarChartIcon />} label="Bar Charts" variant="outlined" />
              <Chip icon={<ScheduleIcon />} label="Hourly Distribution" variant="outlined" />
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Events;
